import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Pencil,
  Check,
  X as XIcon,
  GripVertical,
  Mic,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FIELD_TEMPLATES } from "@/lib/fieldTemplates";
import { api } from "@/lib/api";

const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "rich_text", label: "Rich text" },
  { value: "editor", label: "Editor (formatted)" },
  { value: "scripture_reference", label: "Scripture reference" },
  { value: "list", label: "Bulleted list" },
  { value: "numbered_list", label: "Numbered list" },
];

// Generate a safe field_key from a human-readable field_name.
function slugifyKey(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9_\s-]/g, "")
    .trim()
    .replace(/[\s-]+/g, "_")
    .slice(0, 40);
}

function FieldTypeBadge({ type }) {
  const label = FIELD_TYPES.find((t) => t.value === type)?.label || type;
  return (
    <Badge variant="secondary" className="text-[10px] font-medium">
      {label}
    </Badge>
  );
}

function FieldEditor({ initial, onCancel, onSave, otherKeys = [] }) {
  const [name, setName] = useState(initial?.field_name || "");
  const [key, setKey] = useState(initial?.field_key || "");
  const [type, setType] = useState(initial?.field_type || "text");
  const [required, setRequired] = useState(!!initial?.required);
  const [allowAudio, setAllowAudio] = useState(initial?.allow_audio ?? true);
  const [keyTouched, setKeyTouched] = useState(!!initial?.field_key);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Auto-suggest field_key as name changes — only if the user hasn't manually edited it
  useEffect(() => {
    if (!keyTouched) setKey(slugifyKey(name));
  }, [name, keyTouched]);

  const validate = () => {
    if (!name.trim()) return "Field name is required.";
    const k = key.trim();
    if (!k) return "Field key is required.";
    if (!/^[a-z][a-z0-9_]*$/.test(k))
      return "Field key must start with a letter and contain only lowercase letters, digits, and underscores.";
    if (otherKeys.includes(k))
      return "Another field in this type already uses this key.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setSaving(true);
    try {
      await onSave({
        field_name: name.trim(),
        field_key: key.trim(),
        field_type: type,
        required,
        allow_audio: allowAudio,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-border bg-muted/30 p-3 space-y-3"
      data-testid="field-editor"
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="field-name" className="text-xs">Display name</Label>
          <Input
            id="field-name"
            data-testid="field-name-input"
            placeholder="e.g. Scripture"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="field-key" className="text-xs">
            Key <span className="text-muted-foreground">(internal id)</span>
          </Label>
          <Input
            id="field-key"
            data-testid="field-key-input"
            placeholder="e.g. scripture"
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
              setKeyTouched(true);
            }}
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Type</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="field-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FIELD_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Switch
            data-testid="field-required-toggle"
            checked={required}
            onCheckedChange={setRequired}
          />
          <span>Required</span>
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Switch
            data-testid="field-audio-toggle"
            checked={allowAudio}
            onCheckedChange={setAllowAudio}
          />
          <span>Allow audio narration</span>
        </label>
      </div>

      {error && (
        <p className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="size-3.5" /> {error}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={saving}
          data-testid="field-cancel-btn"
        >
          <XIcon className="size-3.5 mr-1" /> Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={saving}
          data-testid="field-save-btn"
        >
          <Check className="size-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

function FieldRow({ field, position, total, onEdit, onDelete, onMove, disabled }) {
  return (
    <div
      data-testid={`field-row-${field.id}`}
      className="flex items-center gap-2 rounded-lg border border-border/70 bg-background p-2.5 hover:bg-muted/30 transition-colors"
    >
      <GripVertical className="size-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{field.field_name}</span>
          <span className="text-[10px] text-muted-foreground font-mono">
            {field.field_key}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          <FieldTypeBadge type={field.field_type} />
          {field.required && (
            <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-300">
              Required
            </Badge>
          )}
          {field.allow_audio && (
            <Badge variant="outline" className="text-[10px] inline-flex items-center gap-0.5">
              <Mic className="size-2.5" /> Audio
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={disabled || position === 0}
          onClick={() => onMove(field.id, -1)}
          aria-label="Move up"
          data-testid={`field-up-${field.id}`}
        >
          <ArrowUp className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={disabled || position === total - 1}
          onClick={() => onMove(field.id, +1)}
          aria-label="Move down"
          data-testid={`field-down-${field.id}`}
        >
          <ArrowDown className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={disabled}
          onClick={() => onEdit(field)}
          aria-label="Edit field"
          data-testid={`field-edit-${field.id}`}
        >
          <Pencil className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-destructive hover:text-destructive hover:bg-destructive/10"
          disabled={disabled}
          onClick={() => onDelete(field)}
          aria-label="Delete field"
          data-testid={`field-delete-${field.id}`}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function FieldsManager({ typeId }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);  // null = none, "new" = creating
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [templateToConfirm, setTemplateToConfirm] = useState(null);  // {template, replaceMode}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/devotional-fields", { params: { type_id: typeId } });
      data.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
      setFields(data);
    } catch (err) {
      toast.error(err.userMessage || "Failed to load fields");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (typeId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId]);

  const otherKeys = (excludeId) =>
    fields.filter((f) => f.id !== excludeId).map((f) => f.field_key);

  const handleSaveField = async (payload) => {
    setBusy(true);
    try {
      if (editingId === "new") {
        await api.post("/devotional-fields", {
          ...payload,
          devotional_type_id: typeId,
          display_order: fields.length,
        });
        toast.success("Field added");
      } else {
        await api.put(`/devotional-fields/${editingId}`, {
          ...payload,
          devotional_type_id: typeId,
          display_order: fields.find((f) => f.id === editingId)?.display_order ?? 0,
        });
        toast.success("Field updated");
      }
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(err.userMessage || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    setBusy(true);
    try {
      await api.delete(`/devotional-fields/${confirmDeleteId}`);
      toast.success("Field deleted");
      setConfirmDeleteId(null);
      await load();
    } catch (err) {
      toast.error(err.userMessage || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const handleMove = async (id, delta) => {
    const idx = fields.findIndex((f) => f.id === id);
    const swapIdx = idx + delta;
    if (idx < 0 || swapIdx < 0 || swapIdx >= fields.length) return;
    const reordered = [...fields];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    // Re-number display_order
    const payload = reordered.map((f, i) => ({ id: f.id, display_order: i }));
    setFields(reordered.map((f, i) => ({ ...f, display_order: i }))); // optimistic
    try {
      await api.post("/devotional-fields/reorder", payload);
    } catch (err) {
      toast.error(err.userMessage || "Reorder failed");
      await load(); // resync
    }
  };

  const handleApplyTemplate = async (template, replaceMode) => {
    setBusy(true);
    try {
      await api.post("/devotional-fields/bulk", {
        devotional_type_id: typeId,
        replace_existing: replaceMode,
        fields: template.fields.map((f) => ({ ...f, devotional_type_id: typeId })),
      });
      toast.success(`${template.label} template applied (${template.fields.length} fields)`);
      setTemplateToConfirm(null);
      await load();
    } catch (err) {
      toast.error(err.userMessage || "Could not apply template");
    } finally {
      setBusy(false);
    }
  };

  const requestTemplate = (template) => {
    // If there are no fields yet, just apply. Otherwise confirm replace vs append.
    if (fields.length === 0) {
      handleApplyTemplate(template, false);
    } else {
      setTemplateToConfirm(template);
    }
  };

  const editingField = editingId && editingId !== "new"
    ? fields.find((f) => f.id === editingId)
    : null;

  return (
    <div className="space-y-3" data-testid="fields-manager">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold">Fields</h4>
          <p className="text-xs text-muted-foreground">
            Define the sections each devotional of this type will have (e.g. Topic, Scripture, Message,
            Questions, Reflection). Order matters — it's the reading order in the app.
          </p>
        </div>
        {!editingId && (
          <div className="flex items-center gap-1.5 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="apply-template-btn"
                  disabled={busy}
                >
                  <Sparkles className="size-3.5 mr-1" /> Templates
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Start from a template</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {FIELD_TEMPLATES.map((tpl) => (
                  <DropdownMenuItem
                    key={tpl.id}
                    onClick={() => requestTemplate(tpl)}
                    data-testid={`template-${tpl.id}`}
                    className="flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                  >
                    <span className="font-medium text-sm">{tpl.label}</span>
                    <span className="text-[11px] text-muted-foreground leading-snug whitespace-normal">
                      {tpl.description}
                    </span>
                    <span className="text-[10px] text-muted-foreground mt-0.5">
                      {tpl.fields.length} fields
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setEditingId("new")}
              data-testid="add-field-btn"
            >
              <Plus className="size-3.5 mr-1" /> Add field
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 rounded-lg bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {fields.length === 0 && editingId !== "new" && (
            <div className="rounded-lg border border-dashed border-border p-6 text-center">
              <Sparkles className="size-7 mx-auto text-muted-foreground" />
              <p className="mt-2 text-sm font-medium">No fields yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Use the <span className="font-medium">Templates</span> button above for a one-click
                Bible Study, Daily Devotional, Sermon Notes, or Topical Study layout — or click
                <span className="font-medium"> Add field</span> to build your own from scratch.
              </p>
            </div>
          )}

          {fields.map((f, idx) => (
            editingId === f.id ? (
              <FieldEditor
                key={f.id}
                initial={f}
                otherKeys={otherKeys(f.id)}
                onCancel={() => setEditingId(null)}
                onSave={handleSaveField}
              />
            ) : (
              <FieldRow
                key={f.id}
                field={f}
                position={idx}
                total={fields.length}
                disabled={busy || !!editingId}
                onEdit={(field) => setEditingId(field.id)}
                onDelete={(field) => setConfirmDeleteId(field.id)}
                onMove={handleMove}
              />
            )
          ))}

          {editingId === "new" && (
            <FieldEditor
              initial={null}
              otherKeys={otherKeys(null)}
              onCancel={() => setEditingId(null)}
              onSave={handleSaveField}
            />
          )}
        </div>
      )}

      {/* Template-replace confirmation: only shown when the type already has fields */}
      <AlertDialog open={!!templateToConfirm} onOpenChange={(o) => !o && setTemplateToConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply "{templateToConfirm?.label}" template?</AlertDialogTitle>
            <AlertDialogDescription>
              This type already has {fields.length} field{fields.length === 1 ? "" : "s"}. Choose how
              the template should be applied:
              <span className="block mt-2 space-y-1">
                <span className="block"><strong>Append</strong> — keep all existing fields and add the template's fields at the end.</span>
                <span className="block"><strong>Replace</strong> — delete all existing fields first (existing devotional data is preserved on disk but won't render).</span>
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex-row !justify-end gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => handleApplyTemplate(templateToConfirm, false)}
              disabled={busy}
              data-testid="template-append-btn"
            >
              Append
            </Button>
            <AlertDialogAction
              onClick={() => handleApplyTemplate(templateToConfirm, true)}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="template-replace-btn"
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Inline delete confirmation */}
      {confirmDeleteId && (
        <div
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
          data-testid="field-delete-confirm"
        >
          <p className="font-medium">Delete this field?</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Existing devotionals will keep their stored value but it won't be rendered anywhere.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmDeleteId(null)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDelete}
              disabled={busy}
              data-testid="field-delete-confirm-btn"
            >
              {busy ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
